import React from "react";
import { t } from "../../../components/CustomComponent.js";
import { typeMeta, isLandType, parkingTypes, parkingCount } from "../../../components/asset_option.js";
import ICON from "../icons.js";
import { money, isRent, isSale, monthWord, directionWord } from "../property_util.js";

/* ============================================================================
 * PropertyHead — หัวข้อประกาศ : รหัสทรัพย์ / ชื่อ / ทำเล / สเปกย่อ / ราคา
 *
 * สเปกย่อเรียงเป็นแถบเดียว แยกช่องด้วยเส้นบาง ๆ ให้กวาดตาอ่านได้รวดเดียว
 * ที่ดินไม่มีห้องนอน-ห้องน้ำ-ชั้น จึงสลับไปโชว์เนื้อที่ดินแทนโดยอัตโนมัติ
 * ========================================================================== */

/* 1 ช่องของแถบสเปก */
const Spec = ({ icon, value, label }) => (
  <div className="hm-pd-spec">
    <span className="hm-pd-spec__ico hm-ico">{icon}</span>
    <span className="hm-pd-spec__value">{value}</span>
    <span className="hm-pd-spec__label">{label}</span>
  </div>
);

export default function PropertyHead({ data, favorite, onFavorite, onShare }) {
  const meta = typeMeta(data.type);
  const direction = directionWord(data.direction);
  const land = isLandType(data.type);

  /* แถบสเปก — โชว์เฉพาะช่องที่มีค่าจริง จะได้ไม่มีช่อง "-" ว่าง ๆ ให้รก */
  const specs = [];
  if (land) {
    if (Number(data.land_area)) specs.push({ icon: ICON.area, value: Number(data.land_area).toLocaleString(), label: t("sqw") });
  } else {
    if (Number(data.bedroom)) specs.push({ icon: ICON.bed, value: data.bedroom, label: t("bedroom") });
    if (Number(data.bathroom)) specs.push({ icon: ICON.bath, value: data.bathroom, label: t("bathroom") });
    if (Number(data.area)) specs.push({ icon: ICON.area, value: data.area, label: t("sqm") });
    if (Number(data.floor)) specs.push({ icon: ICON.floor, value: data.floor, label: t("floor_label") });
    if (data.building) specs.push({ icon: ICON.building, value: data.building, label: t("detail_building") });
  }
  // ที่จอดรถบอกเป็นประเภทที่จอดได้ ระบุจำนวนคันไว้จึงต่อท้าย — ติ๊กทั้งคู่รวมเป็นช่องเดียว
  const parking = parkingTypes(data)
    .map((e) => (parkingCount(data, e) ? `${t(e.label)} ${parkingCount(data, e)} ${t("car_unit")}` : t(e.label)))
    .join(" · ");
  if (parking) specs.push({ icon: ICON.car, value: parking, label: t("detail_parking") });
  if (direction) specs.push({ icon: ICON.compass, value: direction, label: t("direction") });

  return (
    <section className="hm-pd-head">
      <div className="hm-pd-head__top">
        <div className="hm-pd-head__meta">
          <span className="hm-pd-code">
            {t("detail_code")} {data.code}
          </span>
          <span className="hm-pd-chip">{t(meta.label)}</span>
        </div>

        <div className="hm-pd-head__actions">
          <button
            type="button"
            className={`hm-pd-action ${favorite ? "is-on" : ""}`}
            onClick={onFavorite}
            aria-pressed={!!favorite}
          >
            <span className="hm-ico">{ICON.heart}</span>
            {t(favorite ? "detail_saved" : "detail_save")}
          </button>
          <button type="button" className="hm-pd-action" onClick={onShare}>
            <span className="hm-ico">{ICON.share}</span>
            {t("detail_share")}
          </button>
        </div>
      </div>

      {/* หัวข้อที่ผู้ดูแลตั้งเองมาก่อน ไม่มีจึงตกมาที่ชื่อโครงการ (ฝั่ง API เรียงให้แล้วใน title) */}
      <h1 className="hm-pd-title">{data.title || data.project}</h1>

      <div className="hm-pd-head__loc">
        <span className="hm-ico">{ICON.marker}</span>
        {data.zone}
      </div>

      {/* ---------------------------------------------------------- สเปกย่อ */}
      {!!specs.length && (
        <div className="hm-pd-specs">
          {specs.map((item, i) => (
            <Spec key={`${item.label}-${i}`} icon={item.icon} value={item.value} label={item.label} />
          ))}
        </div>
      )}

      {/* ------------------------------------------------------------- ราคา */}
      <div className="hm-pd-price">
        <div className="hm-pd-price__row">
          {isRent(data.deals) && !!data.rent && (
            <div className="hm-pd-price__main">
              {money(data.rent)}
              <small>{t("baht_per_month")}</small>
            </div>
          )}
          {isSale(data.deals) && !!data.price && (
            <div className={`hm-pd-price__main ${isRent(data.deals) ? "hm-pd-price__main--second" : ""}`}>
              {/* โครงการเปิดใหม่บอกได้แค่ราคาเริ่มต้น ไม่ใช่ราคาปิดของห้องใดห้องหนึ่ง */}
              {!!data.price_from && <small className="hm-pd-price__from">{t("home_price_start")}</small>}
              {money(data.price)}
              <small>{t("unit_baht")}</small>
            </div>
          )}
        </div>

        {/* เงื่อนไขที่ผู้เช่าต้องรู้ตั้งแต่แรก — วางติดราคาเพื่อไม่ให้รู้ทีหลังแล้วเสียความรู้สึก */}
        <div className="hm-pd-price__terms">
          {isRent(data.deals) && !!Number(data.min_contract_month) && (
            <span className="hm-pd-term">
              <span className="hm-ico">{ICON.file}</span>
              {t("detail_min_contract")} {monthWord(data.min_contract_month)}
            </span>
          )}
          {isRent(data.deals) && !!Number(data.deposit) && (
            <span className="hm-pd-term">
              <span className="hm-ico">{ICON.shield}</span>
              {t("detail_deposit")} {Math.round(Number(data.deposit) / Number(data.rent || 1))} {t("month_unit")}
              {!!Number(data.advance_month) && ` + ${t("detail_advance")} ${data.advance_month} ${t("month_unit")}`}
            </span>
          )}
          {isSale(data.deals) && !!data.price && (
            <span className="hm-pd-term">
              <span className="hm-ico">{ICON.file}</span>
              {t("detail_transfer_split")}
            </span>
          )}
        </div>
      </div>
    </section>
  );
}
